import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Dialog,
  Typography,
  Grid,
  Button,
  CircularProgress
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useLazyGetPartnerAddOnsQuery,useBuyAddonsMutation } from 'services/user-service'
import Divider from '@mui/material/Divider'
import { planSettingFormFields } from 'config'
import { toast } from "react-hot-toast";

const AddOnSettingModel = ({ onClose, open, allWorkspaces }) => {
  const [addBuyAddOns] = useBuyAddonsMutation();
  const [getPartnerAddOnsData] = useLazyGetPartnerAddOnsQuery();
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedWorkspace, setSelectedWorkspace] = useState(null)
  const section = planSettingFormFields[0];

  useEffect(() => {
    if (allWorkspaces && allWorkspaces.length > 0) {
      setSelectedWorkspace(allWorkspaces[0])
    }
  }, [allWorkspaces])

  const sendUpdatedPlan = async (updatedData) => {
    try {
      setIsSubmitting(true)
      await addBuyAddOns(updatedData);
    } catch (error) {
      console.error(error)
    }
    setIsSubmitting(false)
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      activeLeads: 0,
      monthlyEmails: 0,
      aiWriterCredits: 0,
      leadsCredits: 0,
      bounceCredit: 0,
      linkedinCredits: 0,
      dailyLeadLimit: 0,
      emailAccountsLimit: 0,
      savedContacts: 0,
      dealPipeline: 0,
      dataEnrichmentCredits: 0,
      buyerIntendDataTopiscs: 0,
      selectedWorkspace: selectedWorkspace?._id || '',
    },
    validationSchema: Yup.object({
      activeLeads: Yup.number().integer(),
      monthlyEmails: Yup.number().integer(),
      aiWriterCredits: Yup.number().integer(),
      leadsCredits: Yup.number().integer(),
      linkedinCredits: Yup.number().integer(),
      bounceCredit: Yup.number().integer(),
      dailyLeadLimit: Yup.number().integer(),
      emailAccountsLimit: Yup.number().integer(),
      savedContacts: Yup.number().integer(),
      dealPipeline: Yup.number().integer(),
      dataEnrichmentCredits: Yup.number().integer(),
      buyerIntendDataTopiscs: Yup.number().integer(),
      selectedWorkspace: Yup.string().required('Workspace is required')
    }),
    onSubmit: async (values) => {
      await sendUpdatedPlan(values)
      await onClose()
    }
  })

  useEffect(() => {
    const fetchWorkspaceData = async () => {
      if (selectedWorkspace) {
        if (!selectedWorkspace?._id) return;
        try {
          const addonsData  = await getPartnerAddOnsData(selectedWorkspace?._id).unwrap();
          
          const usage = addonsData?.addons
            .filter(item => item.planId === "price_partnersCustomBuyOns" && item.name && typeof item.quantity === "number")
            .reduce((acc, item) => {
              acc[item.name] = item.quantity;
              return acc;
          }, {});

          formik.setValues({
            ...formik.values,
            selectedWorkspace: selectedWorkspace._id,
            activeLeads: usage?.activeLeads || 0,
            monthlyEmails: usage?.monthlyEmails || 0,
            aiWriterCredits: usage?.aiWriterCredits || 0,
            leadsCredits: usage?.leadsCredits || 0,
            linkedinCredits: usage?.linkedinCredits || 0,
            bounceCredit: usage?.bounceCredit || 0,
            dailyLeadLimit: usage?.dailyLeadLimit || 0,
            emailAccountsLimit: usage?.emailAccountsLimit || 0,
            savedContacts: usage?.savedContacts || 0,
            dealPipeline: usage?.dealPipeline || 0,
            dataEnrichmentCredits: usage?.dataEnrichmentCredits || 0,
            buyerIntendDataTopiscs: usage?.buyerIntendDataTopiscs || 0,
          });
        } catch (error) {
          toast.error(`${error.message || "Error fetching workspace data"}`)
        }
      }
    }

    fetchWorkspaceData()
  }, [selectedWorkspace])

  const handleClose = () => {
    onClose()
  }

  const handleKeyDown = (e) => {
    // Prevent entering invalid characters
    const regex = /^[+-.]*$/ // Only allow digits
    if (regex.test(e.key)) {
      e.preventDefault()
    }
  }

  return (
    <>
      <Dialog onClose={handleClose} open={open}>
        <form
          noValidate
          onSubmit={formik.handleSubmit}
          className={{ width: '500px' }}
        >
          <Box
            maxWidth='sm'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: 'full',
              margin: '1rem',
              bgcolor: 'white',
              padding: '1rem'
            }}
          >
            <Typography
              variant='h3'
              component='h2'
              sx={{ cursor: 'pointer', color: 'rgba(40, 40, 123, 1)' }}
            >
              AddOns Settings
            </Typography>
            {
              <Box key={section.title} sx={{ mt: 3 }}>
                {!(section?.dateFields || section?.workspaceField) &&
                  <Divider sx={{ mt: 3, mb: 2 }} textAlign='center'>
                    <Typography variant='h6' color='textSecondary'>
                      {section.title}
                    </Typography>
                  </Divider>}
                <Grid container spacing={2}>
                  {section.textFields?.map((field) => (
                    <React.Fragment key={field.name}>
                      <Grid item xs={12} md={6} key={field.name}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              fontWeight: 700,
                              lineHeight: '26px',
                              color: 'rgb(129, 129, 176)'
                            }}
                          >
                            {field.label}
                          </Typography>
                        </Box>
                        <TextField
                          fullWidth
                          variant='outlined'
                          placeholder={field.label}
                          name={field.name}
                          value={formik.values[field.name]}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          onKeyDown={handleKeyDown}
                          error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                          helperText={formik.touched[field.name] && formik.errors[field.name]}
                          sx={{
                            '& div': {
                              pl: 0.3,
                              borderRadius: '0.7rem',
                              width: '20rem',
                              mt: '-0.2rem'
                            },
                            '& div fieldset': {
                              border: '1px solid #E4E4E5'
                            },

                            '& div input': {
                              py: 1.3,
                              fontSize: '13px',
                              fontWeight: 400,
                              lineHeight: '16px',
                              letterSpacing: '0em',
                              '&::placeholder': {
                                color: 'rgba(40, 40, 123, 0.5)'
                              }
                            }
                          }}
                        />
                      </Grid>
                    </React.Fragment>
                  ))}
                </Grid>
              </Box>
            }

            <Box sx={{ display: 'flex', justifyContent: 'end', mt: '2rem' }}>
              <SaveButton
                onClick={formik.handleSubmit}
                isSubmitting={isSubmitting}
              />
            </Box>
          </Box>
        </form>
      </Dialog>
    </>
  )
}

export default AddOnSettingModel

const SaveButton = ({ onClick, isSubmitting }) => {
  return (
    <Button
      sx={{
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '18px',
        py: 1,
        px: 2
      }}
      variant='contained'
      type='submit'
      onClick={onClick}
    >
      {isSubmitting
        ? (
          <>
            <CircularProgress
              color='inherit'
              size={20}
              thickness={5}
              sx={{ mr: 1 }}
            />
            Saving
          </>
          )
        : (
          <>Save</>
          )}
    </Button>
  )
}
